import React, { useState, useEffect, useRef, useCallback } from "react";
import { Toast } from "primereact/toast";
import { FilterMatchMode } from "primereact/api";
import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import { ProgressSpinner } from "primereact/progressspinner";
import usePageTitle from "../usePageTitle";
import {
  fetchAllForms,
  fetchUserCreatedForms,
  addFilter,
  deleteFilter,
  editFilter,
  fetchFilterPreview,
  fetchAllFilters,
  fetchSuperuserFilters,
} from "./api";
import FiltersHeaderComponent from "./FiltersHeaderComponent";
import FiltersDatatableComponent from "./FiltersDatatableComponent";
import FiltersDialog from "./FiltersDialog";
import FilterPreviewDialog from "./FiltersPreviewDialog";
import DeleteFilterDialog from "./DeleteFilterDialog";

import "./Filter.css";

export default function Filters() {
  usePageTitle("Filters - SkeyeForm™");

  const [filters, setFilters] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [previewDialogVisible, setPreviewDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [filterToDelete, setFilterToDelete] = useState(null);
  const [forms, setForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [previewResults, setPreviewResults] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [tableFilters, setTableFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  const [filterName, setFilterName] = useState("");
  const [filterRules, setFilterRules] = useState([
    { field: null, operator: "equals", value: "" },
  ]);
  const [editingFilter, setEditingFilter] = useState(null);

  const toast = useRef(null);

  useEffect(() => {
    if (filters.length > 0) {
      const filtered = filters.filter((filter) => {
        const searchLower = globalFilterValue.toLowerCase();
        return (
          filter.name.toLowerCase().includes(searchLower) ||
          filter.formTitle?.toLowerCase().includes(searchLower) ||
          filter.createdAt?.toString().includes(searchLower)
        );
      });
      setFilteredData(filtered.slice(first, first + rows));
      setTotalRecords(filtered.length);
    } else {
      setFilteredData([]);
      setTotalRecords(0);
    }
  }, [globalFilterValue, filters, first, rows]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      let currentUserId = null;
      let userRoles = [];

      try {
        const storedUser = localStorage.getItem("userData");
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          currentUserId = parsedUser?.id ?? null;
          userRoles = Array.isArray(parsedUser?.roles)
            ? parsedUser.roles.map((role) => role.name)
            : [];
        }
      } catch (storageError) {
        console.error("Error parsing user data:", storageError);
      }

      const isAdmin = userRoles.includes("ROLE_ADMIN");
      const isSuperUser = userRoles.includes("ROLE_SUPERUSER");

      // Fetch filters based on role
      let fetchedFilters = [];
      if (isAdmin) {
        fetchedFilters = await fetchAllFilters();
      } else if (isSuperUser) {
        fetchedFilters = await fetchSuperuserFilters();
      }

      // Fetch forms based on role
      let visibleForms = [];
      if (isAdmin) {
        const allForms = await fetchAllForms();
        visibleForms = allForms.filter((form) => !form.softDelete);
      } else if (isSuperUser) {
        const userForms = await fetchUserCreatedForms();
        visibleForms = userForms.filter((form) => !form.softDelete);
      }

      setForms(visibleForms);
      setFilters(fetchedFilters);
      setTotalRecords(fetchedFilters.length);
      setFilteredData(fetchedFilters.slice(0, rows));
    } catch (error) {
      console.error("Error loading data:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load data",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [rows]);

  // Add this useEffect to call loadData on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadFilledFormsData = async (formId) => {
    try {
      setLoading(true);
      const selectedFormData = forms.find((form) => form.id === formId);

      if (selectedFormData && selectedFormData.properties) {
        const fields = Object.entries(selectedFormData.properties)
          .filter(([_, prop]) => prop.updated === true)
          .map(([key, prop]) => ({
            label: prop.key || key,
            value: key,
            type: prop.type || "text",
          }));

        setFormFields(fields);

        if (fields.length === 0) {
          toast.current.show({
            severity: "warn",
            summary: "Warning",
            detail: "No updated fields available for filtering in this form.",
            life: 3000,
          });
        }
      } else {
        setFormFields([]);
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail: "No properties found for this form.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error loading filled forms:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load form data",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const previewFilterFromDialog = async (formId, rules) => {
    try {
      const filterData = {
        rules: rules
          .filter((rule) => rule.field)
          .map((rule) => ({
            field: rule.field,
            operator: rule.operator,
            value: rule.value,
          })),
      };

      const results = await fetchFilterPreview(formId, filterData);

      setPreviewResults(results);
      setPreviewDialogVisible(true);
    } catch (error) {
      console.error("Error previewing filter:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to preview filter",
        life: 3000,
      });
    }
  };

  const handleFormChange = (e) => {
    const form = e.value;
    setSelectedForm(form);
    if (form) {
      loadFilledFormsData(form.id);
    } else {
      setFormFields([]);
    }
  };

  const addRule = () => {
    setFilterRules([
      ...filterRules,
      { field: null, operator: "equals", value: "" },
    ]);
  };

  const removeRule = (index) => {
    const newRules = filterRules.filter((_, i) => i !== index);
    setFilterRules(newRules);
  };

  const updateRule = (index, field, value) => {
    const newRules = [...filterRules];
    newRules[index][field] = value;
    setFilterRules(newRules);
  };

  const saveFilter = async () => {
    if (!filterName.trim()) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter a filter name",
        life: 3000,
      });
      return;
    }

    if (!selectedForm) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please select a form",
        life: 3000,
      });
      return;
    }

    if (filterRules.every((rule) => !rule.field)) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please configure at least one filter rule",
        life: 3000,
      });
      return;
    }

    try {
      setLoading(true);

      if (editingFilter) {
        for (let i = 0; i < filterRules.length; i++) {
          const rule = filterRules[i];

          if (rule.field) {
            const filterData = {
              field: rule.field,
              operator: rule.operator,
              value: rule.value,
              name: filterName,
            };
            if (rule.id && editingFilter.rules[i]?.id === rule.id) {
              await editFilter(selectedForm.id, rule.id, filterData);
            } else {
              if (editingFilter.rules[i]?.id) {
                await deleteFilter(selectedForm.id, editingFilter.rules[i].id);
              }
              await addFilter(selectedForm.id, { rules: [filterData] });
            }
          }
        }

        if (editingFilter.rules.length > filterRules.length) {
          for (
            let i = filterRules.length;
            i < editingFilter.rules.length;
            i++
          ) {
            if (editingFilter.rules[i]?.id) {
              await deleteFilter(selectedForm.id, editingFilter.rules[i].id);
            }
          }
        }
      } else {
        const filterData = {
          rules: filterRules
            .filter((rule) => rule.field)
            .map((rule) => ({
              field: rule.field,
              operator: rule.operator,
              value: rule.value,
              name: filterName,
            })),
        };

        await addFilter(selectedForm.id, filterData);
      }

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: editingFilter
          ? "Filter updated successfully"
          : "Filter created successfully",
        life: 3000,
      });

      await loadData();
      closeDialog();
    } catch (error) {
      console.error("Error saving filter:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save filter",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (filter = null) => {
    if (filter) {
      setEditingFilter(filter);
      setFilterName(filter.name);
      const form = forms.find((f) => f.id === filter.formId);
      setSelectedForm(form);
      setFilterRules(filter.rules);
      if (form) {
        loadFilledFormsData(form.id);
      }
    } else {
      setEditingFilter(null);
      setFilterName("");
      setSelectedForm(null);
      setFilterRules([{ field: null, operator: "equals", value: "" }]);
      setFormFields([]);
    }
    setDialogVisible(true);
  };

  const closeDialog = () => {
    setDialogVisible(false);
    setEditingFilter(null);
    setFilterName("");
    setSelectedForm(null);
    setFilterRules([{ field: null, operator: "equals", value: "" }]);
    setFormFields([]);
  };

  const openDeleteDialog = (filter) => {
    setFilterToDelete(filter);
    setDeleteDialogVisible(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogVisible(false);
    setFilterToDelete(null);
  };

  const confirmDeleteFilter = async () => {
    if (!filterToDelete) return;

    try {
      setDeleteLoading(true);

      for (const rule of filterToDelete.rules) {
        await deleteFilter(filterToDelete.formId, rule.id);
      }

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Filter deleted successfully",
        life: 3000,
      });

      closeDeleteDialog();
      await loadData();
    } catch (error) {
      console.error("Error deleting filter:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete filter",
        life: 3000,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const applyFilter = async (filter) => {
    try {
      const filterData = {
        rules: filter.rules.map((rule) => ({
          field: rule.field,
          operator: rule.operator,
          value: rule.value,
        })),
      };
      const results = await fetchFilterPreview(filter.formId, filterData);

      setPreviewResults(results);
      setPreviewDialogVisible(true);
    } catch (error) {
      console.error("Error applying filter:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to apply filter",
        life: 3000,
      });
    }
  };

  const onPageChange = (e) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);
    setFirst(0);
  };

  const clearFilter = () => {
    setGlobalFilterValue("");
    setTableFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });
  };

  const header = (
    <FiltersHeaderComponent
      globalFilterValue={globalFilterValue}
      onGlobalFilterChange={onGlobalFilterChange}
      clearFilter={clearFilter}
      onOpenDialog={() => openDialog()}
    />
  );

  return (
    <div className="table-page">
      <Toast ref={toast} />

      <div className="title">
        <h1>Filters</h1>
        <div className="title-bar"></div>
      </div>

      {loading && !dialogVisible ? (
        <div className="loader-container">
          <ProgressSpinner
            style={{ width: "40px", height: "40px" }}
            strokeWidth="2"
            fill="var(--surface-ground)"
            animationDuration=".5s"
          />
          <span
            style={{
              marginLeft: "10px",
              verticalAlign: "middle",
              fontSize: "20px",
              fontFamily: "Poppins",
            }}
          >
            Fetching Data (This Might Take a Minute)...
          </span>
        </div>
      ) : (
        <FiltersDatatableComponent
          data={filteredData}
          filters={tableFilters}
          header={header}
          loading={false}
          first={first}
          rows={rows}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          onApplyFilter={applyFilter}
          onEditFilter={openDialog}
          onDeleteFilter={openDeleteDialog}
        />
      )}

      <FiltersDialog
        visible={dialogVisible}
        onHide={closeDialog}
        editingFilter={editingFilter}
        filterName={filterName}
        setFilterName={setFilterName}
        selectedForm={selectedForm}
        handleFormChange={handleFormChange}
        forms={forms}
        formFields={formFields}
        filterRules={filterRules}
        addRule={addRule}
        removeRule={removeRule}
        updateRule={updateRule}
        loading={loading}
        onSave={saveFilter}
        onPreview={previewFilterFromDialog}
      />

      <FilterPreviewDialog
        visible={previewDialogVisible}
        onHide={() => setPreviewDialogVisible(false)}
        previewResults={previewResults}
      />

      <DeleteFilterDialog
        visible={deleteDialogVisible}
        onHide={closeDeleteDialog}
        filter={filterToDelete}
        onConfirm={confirmDeleteFilter}
        loading={deleteLoading}
      />
    </div>
  );
}
